<script lang="ts">
  /**
   * The on-screen buttons. All the release-safety logic
   * (`docs/KNOWN-BUGS.md` item 3) lives in `input/touch-controls.ts`; this
   * component only renders the buttons with the `data-action` attributes
   * that module looks for, and exposes its root element to the parent so the
   * parent can bind an `InputSession` to it. `bindTouchControls` selects
   * `[data-action]` elements generically — it does not care how they are
   * grouped or sized.
   *
   * ## Why `root` is a `$bindable` prop and not a callback
   *
   * A child's `$effect` runs *before* its parent's `onMount`, and the parent
   * creates the `InputSession` in `onMount`: a callback fired from here would
   * arrive before there is a session to bind to, and nothing would ever
   * listen. With a `$bindable` prop the parent holds the element in its own
   * `$state` and attaches from an effect that reads *both* the element and
   * the session, so whichever arrives second is what triggers the bind
   * (`play/play-input.svelte.ts`, `docs/decisions/0024`).
   *
   * ## The layout
   *
   * The arrangement is the owner's: two large move pads on the left, a 2×2
   * on the right with soft drop at the bottom right, and one rotate pad.
   *
   * - **Two zones, not one row.** `left`/`right` — the movement thumb's job —
   *   sit in a `.zone.move` on the left; `hold`/`rotateCW`/`hardDrop`/
   *   `softDrop` — the action thumb's job — sit in a `.zone.actions` on the
   *   right. `justify-content: space-between` pushes the two zones to the
   *   screen's edges, which puts the empty middle of the bar where a
   *   two-thumb grip naturally rests.
   * - **The move zone is one row of two, and each pad is bigger than the
   *   standard pad.** `--move-pad-width`/`--move-pad-height` are their own
   *   custom properties, not `--pad-size`: the width starts at 72px against
   *   the standard 58px, and the height is exactly as tall as the action
   *   zone's two rows, so `left`/`right` read as one tall target apiece.
   * - **The action zone is a 2×2 in DOM order.** Four pads in a two-column
   *   grid auto-flow into two rows: `hold`/`rotateCW` on top,
   *   `hardDrop`/`softDrop` below. `hardDrop` carries an accent ring;
   *   `hold` is secondary (a muted glyph), so it reads as "not the
   *   button you mash" without being any smaller than its neighbours.
   * - **The look is the tool-button look**: a borderless rounded square on
   *   the quiet fill with an accent glyph. The arrangement above is this
   *   game's own.
   * - **One rotate pad**, with a circular-arrow glyph, rotating clockwise.
   *   The keyboard and a gamepad keep both directions.
   * - **`--pad-size: clamp(--pad-min, 8vw, 4.5rem)`.** `--pad-min` is 58px —
   *   a couple of pixels of headroom over the 56px floor the e2e suite
   *   checks, so sub-pixel layout rounding never reads as a failure. `8vw`
   *   stays under 58px on every phone width this game targets, so the pad
   *   sits at its floor rather than growing with the viewport: two rows of a
   *   *growing* pad is height the duel screen cannot always spare. The pad
   *   only grows past 58px on something roomier than a phone.
   * - **Short-height landscape gets a single row per zone.** Two rows cost
   *   roughly 2.2× a single row's height, which is a fine trade on a tall
   *   portrait phone (the board is width-bound there anyway) and a bad one on
   *   a short landscape screen, where height is the scarce axis. The
   *   `(max-height: 480px) and (orientation: landscape)` block collapses the
   *   action zone to one row of four and drops the move pads back to the
   *   standard height.
   */
  // Not `root?:`. Under `exactOptionalPropertyTypes` an optional prop refuses
  // an explicitly-`undefined` value, and the parent's binding target starts
  // life undefined by definition — so the prop's type says so.
  import GameGlyph from '../glyphs/GameGlyph.svelte';

  let { root = $bindable(undefined) }: { root: HTMLElement | undefined } = $props();
</script>

<div class="touch-controls" data-testid="touch-controls" bind:this={root}>
  <div class="zone move" role="group" aria-label="Movement" data-testid="touch-zone-move">
    <button type="button" class="pad" data-action="left" aria-label="Move left"
      ><span class="face"><GameGlyph name="left" size="fill" /></span></button
    >
    <button type="button" class="pad" data-action="right" aria-label="Move right"
      ><span class="face"><GameGlyph name="right" size="fill" /></span></button
    >
  </div>
  <!-- A 2×2 in DOM order: `hold`/`rotateCW` auto-flow onto the first row,
       `hardDrop`/`softDrop` onto the second, soft drop bottom right. -->
  <div
    class="zone actions"
    role="group"
    aria-label="Piece controls"
    data-testid="touch-zone-actions"
  >
    <button type="button" class="pad hold" data-action="hold" aria-label="Hold"
      ><span class="face"><GameGlyph name="hold" size="fill" /></span></button
    >
    <button type="button" class="pad" data-action="rotateCW" aria-label="Rotate"
      ><span class="face"><GameGlyph name="rotate" size="fill" /></span></button
    >
    <button type="button" class="pad primary drop" data-action="hardDrop" aria-label="Hard drop"
      ><span class="face"><GameGlyph name="hardDrop" size="fill" /></span></button
    >
    <button type="button" class="pad" data-action="softDrop" aria-label="Soft drop"
      ><span class="face"><GameGlyph name="softDrop" size="fill" /></span></button
    >
  </div>
</div>

<style>
  .touch-controls {
    display: none;
    /*
     * 58px, not 56: a couple of pixels of headroom over the floor the e2e
     * suite checks, so sub-pixel layout rounding never reads as a failure.
     *
     * `8vw` rather than something like `16vw` is deliberate: the action
     * zone's four pads (`hold`/`rotateCW`/`hardDrop`/`softDrop`, a 2×2)
     * cannot fit in one row with the move zone's own pads and the zone gap
     * between them on any phone this game targets (four columns at 56px+
     * plus two move pads even at their own smaller floor already crowds a
     * 360px viewport), so two rows are unavoidable in the action zone if
     * every pad clears the floor — and two rows of a *growing* pad is real
     * height the duel screen cannot always spare (its own board shares the
     * screen with an opponent strip, a header and a footer that the solo
     * screen does not have). Locking the pad close to its floor on every
     * phone width this game actually ships on, and only letting it grow past
     * that on something roomier than a phone (past roughly 725px, where
     * `8vw` first clears 58px — a coarse-pointer tablet or a touch-screen
     * desktop), is what keeps "bigger buttons" from quietly costing "less
     * board" on the one screen with the least height to give up.
     */
    --pad-min: 58px;
    --pad-size: clamp(var(--pad-min), 8vw, 4.5rem);
    --pad-gap: min(0.2rem, 1vw);
    --zone-gap: min(1.5rem, 6vw);
    /*
     * The move zone's own two pads. Wider than the standard pad (72px against
     * 58px, growing at 11vw), but not doubled: the move zone still has to sit
     * side by side with the action zone, and a much wider pad would overflow
     * a 360px viewport. The height is exactly the action zone's two rows plus
     * their gap, so the two zones read as the same height.
     */
    --move-pad-min: 72px;
    --move-pad-width: clamp(var(--move-pad-min), 11vw, 5.5rem);
    --move-pad-height: calc(var(--pad-size) * 2 + var(--pad-gap));
    gap: var(--zone-gap);
    align-items: flex-end;
    justify-content: space-between;
    width: 100%;
    padding: 0.2rem max(0.2rem, env(safe-area-inset-right))
      calc(0.2rem + env(safe-area-inset-bottom)) max(0.2rem, env(safe-area-inset-left));
    /* Belt and braces behind the grid arithmetic: whatever ends up in here,
       the bar itself is never a horizontal scroll container. */
    overflow: hidden;
  }

  /* Below the desktop breakpoint the app is a phone-width column, so the
     pads are on; from it up, only where the pointer is a finger. */
  @media (max-width: 1023px), (pointer: coarse) {
    .touch-controls {
      display: flex;
    }
  }

  .zone {
    display: grid;
    grid-template-columns: repeat(2, var(--pad-size));
    grid-auto-rows: var(--pad-size);
    gap: var(--pad-gap);
    flex: 0 0 auto;
  }

  /* The move zone's own track sizes — see the custom properties' own comment
     for why they are bigger than, and independent of, `--pad-size`. Two
     columns, one row: `left`/`right` are the zone's only two pads now. */
  .zone.move {
    grid-template-columns: repeat(2, var(--move-pad-width));
    grid-auto-rows: var(--move-pad-height);
  }

  .pad {
    /* `touch-action: none` is set here *and* inline by `bindTouchControls`.
       Belt and braces on purpose: it is what stops a press from also scrolling
       the page or triggering double-tap zoom, and it is cheap. */
    touch-action: none;
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    user-select: none;
    -webkit-user-select: none;
    width: var(--pad-size);
    height: var(--pad-size);
    display: grid;
    place-items: center;
    /* A tool button: a borderless rounded square on the quiet fill, with its
       glyph in the accent. */
    border-radius: var(--ui-radius-md);
    border: 0;
    padding: 0;
    background: var(--ui-surface-2);
    color: var(--ui-accent);
  }

  .pad:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }

  /* The move zone's own two pads size independently of `--pad-size` — see
     the custom properties' own comment on `.touch-controls`. */
  .zone.move .pad {
    width: var(--move-pad-width);
    height: var(--move-pad-height);
  }

  /* The glyph: 42 % of the pad, a size a thumb reads at a glance. Pointer
     events go to the pad, never the drawing inside it. */
  .face {
    display: block;
    width: calc(var(--pad-size) * 0.42);
    height: calc(var(--pad-size) * 0.42);
    pointer-events: none;
  }

  .zone.move .face {
    width: calc(var(--move-pad-width) * 0.42);
    height: calc(var(--move-pad-width) * 0.42);
  }

  /* The one you mash: singled out with an accent ring, drawn inside the pad
     so it never reaches its neighbour. */
  .pad.primary {
    box-shadow: inset 0 0 0 2.5px var(--ui-accent);
  }

  /* Secondary, not smaller: `hold` keeps the same 56px+ footprint as every
     other pad (so it is still a comfortable target) but reads as the button
     you reach for on purpose rather than the one you mash. */
  .pad.hold {
    color: var(--ui-ink-2);
  }

  .pad:active {
    background: var(--ui-accent);
    color: var(--ui-on-accent);
  }

  /* A short landscape phone has width to spare and no height to spare — the
     opposite of portrait. The action zone collapses from a 2×2 to one row of
     four, and the move zone's pads drop back to the standard pad height. */
  @media (max-height: 480px) and (orientation: landscape) {
    .touch-controls {
      --pad-min: 48px;
      --pad-size: clamp(var(--pad-min), 8vh, 3.5rem);
      --move-pad-min: 48px;
      --move-pad-width: clamp(var(--move-pad-min), 9vh, 3.75rem);
      --move-pad-height: var(--pad-size);
      padding-top: 0.3rem;
      padding-bottom: calc(0.3rem + env(safe-area-inset-bottom));
    }

    .zone.actions {
      grid-template-columns: repeat(4, var(--pad-size));
    }
  }
</style>
